(() => {
  if (customElements.get('hikmati-audio-preview')) return;
  let sharedContext;
  const formatTime = value => {
    if (!Number.isFinite(value) || value < 0) return '–:––';
    const seconds = Math.floor(value);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  };
  customElements.define('hikmati-audio-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        document.addEventListener('visibilitychange', this.visibilityHandler);
        return;
      }
      this.audio = this.querySelector('audio[data-preview-src]');
      this.button = this.querySelector('button');
      this.seek = this.querySelector('input');
      this.status = this.querySelector('[role="status"]');
      this.bars = [...this.querySelectorAll('line')];
      if (!this.audio || !this.button || !this.seek || !this.status) return;
      this.ready = true;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('.hk-audio-preview__controls').hidden = false;
      this.bindAudio();
      this.button.addEventListener('click', () => this.toggle());
      this.seek.addEventListener('input', () => {
        const duration = this.audio.duration;
        if (Number.isFinite(duration) && duration > 0) {
          this.audio.currentTime = duration * Number(this.seek.value) / 1000;
          this.updateTime();
        }
      });
      this.visibilityHandler = () => {
        if (document.hidden) this.stopDrawing();
        else if (!this.audio.paused) this.startDrawing();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    disconnectedCallback() {
      this.audio?.pause();
      this.stopDrawing();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    message(kind) {
      const loading = kind === 'loading';
      this.toggleAttribute('data-loading', loading);
      this.toggleAttribute('data-error', kind === 'error');
      this.button.setAttribute('aria-busy', String(loading));
      this.status.textContent = kind ? this.dataset[kind] : '';
      this.status.hidden = !kind;
    }
    bindAudio() {
      const audio = this.audio;
      ['loadedmetadata', 'durationchange', 'timeupdate'].forEach(event => {
        audio.addEventListener(event, () => this.updateTime());
      });
      audio.addEventListener('play', () => {
        document.querySelectorAll('hikmati-audio-preview').forEach(other => {
          if (other !== this) other.audio?.pause();
        });
        this.toggleAttribute('data-playing', true);
        this.button.setAttribute('aria-label', this.dataset.pause);
      });
      audio.addEventListener('playing', () => {
        this.message('');
        this.startDrawing();
      });
      audio.addEventListener('waiting', () => {
        if (!audio.paused) {
          this.message('loading');
          this.stopDrawing();
        }
      });
      ['pause', 'ended'].forEach(event => audio.addEventListener(event, () => {
        this.toggleAttribute('data-playing', false);
        this.button.setAttribute('aria-label', this.dataset.play);
        this.message('');
        this.stopDrawing();
        this.updateTime();
      }));
      audio.addEventListener('error', () => this.recover(audio));
    }
    updateTime() {
      const audio = this.audio;
      const known = Number.isFinite(audio.duration) && audio.duration > 0;
      const progress = known ? Math.min(1, Math.max(0, audio.currentTime / audio.duration)) : 0;
      this.querySelector('[data-current]').textContent = formatTime(audio.currentTime);
      this.querySelector('[data-duration]').textContent = known ? formatTime(audio.duration) : '–:––';
      this.seek.disabled = !known;
      this.seek.value = String(Math.round(progress * 1000));
      this.seek.setAttribute('aria-valuetext', `${formatTime(audio.currentTime)} / ${known ? formatTime(audio.duration) : '–:––'}`);
      this.bars.forEach((bar, index) => bar.toggleAttribute('data-played', (index + 1) / this.bars.length <= progress));
    }
    prepareAnalysis() {
      if (this.noAnalysis || this.motion.matches || this.analyser) return;
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return;
      try {
        sharedContext ||= new Context();
        const analyser = sharedContext.createAnalyser();
        analyser.fftSize = 256;
        const source = sharedContext.createMediaElementSource(this.audio);
        source.connect(sharedContext.destination);
        source.connect(analyser);
        this.source = source;
        this.analyser = analyser;
        this.samples = new Uint8Array(analyser.fftSize);
      } catch {
        this.noAnalysis = true;
      }
    }
    async toggle() {
      if (!this.audio.paused) {
        this.audio.pause();
        return;
      }
      if (this.pending) return;
      this.pending = true;
      this.message('loading');
      this.prepareAnalysis();
      if (!this.audio.getAttribute('src')) this.audio.src = this.audio.dataset.previewSrc;
      if (this.audio.error) this.audio.load();
      if (this.audio.ended) this.audio.currentTime = 0;
      try {
        const resumed = this.analyser && sharedContext.state !== 'running' ? sharedContext.resume() : Promise.resolve();
        await Promise.all([resumed, this.audio.play()]);
      } catch (error) {
        if (!this.recovering && error.name !== 'AbortError') this.message('error');
      } finally {
        this.pending = false;
      }
    }
    async recover(failedAudio) {
      if (failedAudio !== this.audio) return;
      this.stopDrawing();
      if (this.fallbackUsed) {
        this.message('error');
        this.toggleAttribute('data-playing', false);
        this.button.setAttribute('aria-label', this.dataset.play);
        return;
      }
      this.recovering = true;
      this.fallbackUsed = true;
      this.noAnalysis = true;
      this.source?.disconnect();
      this.analyser = null;
      const replacement = failedAudio.cloneNode(false);
      replacement.removeAttribute('src');
      replacement.removeAttribute('crossorigin');
      failedAudio.replaceWith(replacement);
      this.audio = replacement;
      this.bindAudio();
      replacement.src = replacement.dataset.previewSrc;
      try {
        await replacement.play();
      } catch {
        this.message('error');
        this.toggleAttribute('data-playing', false);
        this.button.setAttribute('aria-label', this.dataset.play);
      } finally {
        this.recovering = false;
      }
    }
    startDrawing() {
      this.stopDrawing();
      if (!this.analyser || this.motion.matches || document.hidden) return;
      let previous = 0;
      const draw = now => {
        if (this.audio.paused || !this.isConnected || document.hidden || this.motion.matches) return;
        if (now - previous >= 65) {
          previous = now;
          this.analyser.getByteTimeDomainData(this.samples);
          this.bars.forEach((bar, index) => {
            const start = Math.floor(index * this.samples.length / this.bars.length);
            const end = Math.floor((index + 1) * this.samples.length / this.bars.length);
            let amplitude = 0;
            for (let sample = start; sample < end; sample++) amplitude = Math.max(amplitude, Math.abs(this.samples[sample] - 128) / 128);
            const height = Math.min(28, Math.max(4, amplitude * 56));
            bar.setAttribute('y1', String(16 - height / 2));
            bar.setAttribute('y2', String(16 + height / 2));
          });
        }
        this.frame = requestAnimationFrame(draw);
      };
      this.frame = requestAnimationFrame(draw);
    }
    stopDrawing() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
})();
